import { useCallback, useRef, useState } from "react";
import { trackUserAction } from "../../../../../../utils/pendo";
import { getEntitySelectionKey } from "./entitySelectionKey";

const dedupeMessages = (messages = []) =>
    [...new Set(messages.filter((message) => typeof message === "string" && message.trim()))];

const sortSuggestionsByScore = (suggestions = []) =>
    [...suggestions].sort((left, right) => (right?.score ?? 0) - (left?.score ?? 0));

const useEntityAiSuggestions = ({ onSuggestEntity, profileId }) => {
    const [aiEntityError, setAiEntityError] = useState("");
    const [aiEntityScore, setAiEntityScore] = useState(null);
    const [aiEntitySuggestions, setAiEntitySuggestions] = useState([]);
    const [aiEntityWarnings, setAiEntityWarnings] = useState([]);
    const [loadingAiEntity, setLoadingAiEntity] = useState(false);
    const aiRequestId = useRef(0);

    const invalidateAiState = useCallback(() => {
        aiRequestId.current += 1;
        setAiEntityError("");
        setAiEntityScore(null);
        setAiEntitySuggestions([]);
        setAiEntityWarnings([]);
        setLoadingAiEntity(false);
    }, []);

    const clearEntityScore = useCallback(() => {
        setAiEntityScore(null);
    }, []);

    const acceptSuggestion = useCallback(({ entities, selectionKey, suggestions }) => {
        const selectedEntity =
            entities.find((entity) => getEntitySelectionKey(entity) === selectionKey) ||
            entities.find((entity) => entity.file_type === selectionKey);
        const suggestionRank = suggestions.findIndex(
            (suggestion) => suggestion.entity_name === selectedEntity?.cdm_entity_name,
        );
        const suggestion = suggestions[suggestionRank];
        if (suggestion) {
            trackUserAction("mapping_entity_suggestion_accepted", {
                suggested_entity: suggestion.entity_name,
                suggestion_score: suggestion.score,
                suggestion_rank: suggestionRank,
            });
        }
        setAiEntityScore(suggestion?.score ?? null);
        return selectionKey;
    }, []);

    const requestSuggestions = useCallback(
        async ({ configSet, entities }) => {
            if (!profileId || !configSet || !onSuggestEntity || !entities.length) return null;
            const currentRequest = ++aiRequestId.current;
            trackUserAction("mapping_entity_suggestion_requested", {
                profile_id: profileId,
                config_set: configSet,
                available_entity_count: entities.length,
            });
            setLoadingAiEntity(true);
            setAiEntityError("");
            setAiEntitySuggestions([]);
            setAiEntityWarnings([]);
            try {
                const result = await onSuggestEntity(profileId, configSet, entities);
                if (currentRequest !== aiRequestId.current) return null;

                const suggestions = sortSuggestionsByScore(result?.suggestions || []);
                const warnings = dedupeMessages(result?.warnings || []);
                setAiEntitySuggestions(suggestions);
                setAiEntityWarnings(warnings);

                const topSuggestion = suggestions[0];
                if (topSuggestion) {
                    const selectedEntity = entities.find(
                        (entity) => entity.cdm_entity_name === topSuggestion.entity_name,
                    );
                    if (selectedEntity) {
                        const resolvedSelectionKey = getEntitySelectionKey(selectedEntity);
                        acceptSuggestion({
                            entities,
                            selectionKey: resolvedSelectionKey,
                            suggestions,
                        });
                        return {
                            selectionKey: resolvedSelectionKey,
                            fileType: selectedEntity.file_type,
                            score: topSuggestion.score,
                        };
                    }
                }

                if (warnings.length) {
                    setAiEntityError(warnings[0]);
                } else if (!suggestions.length) {
                    setAiEntityError("AI returned no valid entity suggestions");
                }
                return null;
            } catch (error) {
                if (currentRequest !== aiRequestId.current) return null;
                setAiEntityError("AI suggestion service unavailable");
                return null;
            } finally {
                if (currentRequest === aiRequestId.current) setLoadingAiEntity(false);
            }
        },
        [acceptSuggestion, onSuggestEntity, profileId],
    );

    return {
        acceptSuggestion,
        aiEntityError,
        aiEntityScore,
        aiEntitySuggestions,
        aiEntityWarnings,
        clearEntityScore,
        invalidateAiState,
        loadingAiEntity,
        requestSuggestions,
    };
};

export default useEntityAiSuggestions;
